import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  type ReactNode,
} from "react";
import { DashboardSection } from "@/components/dashboard-section";
import { PageHeader } from "@/components/page-header";
import { CoolIcon } from "@/components/ui/cool-icon";
import { TabNav } from "@/components/tab-nav";
import { AddAccountButton } from "@/components/add-account/add-account-button";
import {
  listAccounts,
  decodeAccountInfo,
  rematchAccount,
  type AccountRow,
} from "@/lib/api/accounts";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { firmLogoUrl } from "@/lib/firm-logos";
import { Wallet } from "@/components/ui/icons";
import { ChallengeGroupSection } from "@/components/challenge-group-section";
import { groupAccounts } from "@/lib/account-groups";
import { AccountsTable, type AccountView } from "@/components/accounts-table";
import type { AccountCardData } from "@/components/account-card";

/** Snap a balance to the closest canonical account size at-or-below it. */
function roundToCanonical(balance: number): number {
  const sizes = [1000, 2500, 5000, 10000, 15000, 25000, 50000, 100000, 200000];
  return sizes.reduce((best, s) => (s <= balance ? s : best), 1000);
}

const TABS = [
  { key: "grouped", label: "Grouped" },
  { key: "table", label: "Table" },
];

/* ── Page ── */

export function AccountsPage(): ReactNode {
  const [tab, setTab] = useState("grouped");
  const [rawAccounts, setRawAccounts] = useState<AccountRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [matchingId, setMatchingId] = useState<string | null>(null);
  const navigate = useNavigate();

  const refetch = useCallback(() => {
    setLoadError(null);
    listAccounts()
      .then(setRawAccounts)
      .catch((e) => setLoadError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleFindPartner = useCallback(
    async (acc: AccountView): Promise<void> => {
      setMatchingId(acc.id);
      try {
        const result = await rematchAccount(acc.id);
        if (result.paired && result.accountPairSlug) {
          navigate(`/pairs/${encodeURIComponent(result.accountPairSlug)}`);
        } else if (result.proposalSlugs.length > 0) {
          navigate("/pair-approvals");
        } else {
          toast(`No compatible partner for #${acc.slug} yet.`);
        }
      } catch (e) {
        toast.error(
          `Match failed: ${e instanceof Error ? e.message : String(e)}`,
        );
      } finally {
        setMatchingId(null);
      }
    },
    [navigate],
  );

  /**
   * Map the raw /api/accounts rows to the view model. Rows still in
   * the wizard's validation lifecycle (status='pending' / 'validating')
   * DO render in the Grouped view — the operator needs to see a
   * newly-added phase-2 account immediately — but they show with a
   * "Validating" status pill on the card.
   */
  const accounts = useMemo<AccountView[]>(() => {
    if (!rawAccounts || rawAccounts.length === 0) return [];
    return rawAccounts.map((r) => {
      const info = decodeAccountInfo(r);
      const tableStatus: "active" | "paused" | "passed" | "breached" =
        r.outcome === "passed"
          ? "passed"
          : r.outcome === "breached"
            ? "breached"
            : r.status === "paused"
              ? "paused"
              : r.status === "active" ||
                  r.status === "pending" ||
                  r.status === "validating" ||
                  r.status === "failed_validation"
              ? "active"
              : "breached";
      const isInstant = r.challenge_type === "instant" || r.steps === 0;
      const terminalStatusLabel =
        r.outcome === "passed" && isInstant
          ? "Payout"
          : r.outcome === "passed"
            ? "Passed"
            : r.outcome === "breached"
              ? "Breached"
              : undefined;

      // Card renders the raw DB status so validation/pending/paused
      // show the right micro-status; the Table column uses the coarser
      // `tableStatus` so its one-word badge stays readable.
      const card: AccountCardData = {
        id: r.id,
        slug: r.slug,
        pairSlug: r.pair_slug,
        firmId: r.firm_id,
        firmName: r.firm_id,
        firmLogo: firmLogoUrl(r.firm_id) ?? "",
        login: r.login,
        accountLabel: r.account_label,
        accountSize:
          r.account_size ?? (info?.balance ? roundToCanonical(info.balance) : 0),
        balance: info?.balance ?? null,
        finalBalance: r.final_balance,
        currency: info?.currency ?? "USD",
        phase: r.phase,
        challengeType: r.challenge_type,
        status: r.outcome ?? r.status,
        statusLabel: terminalStatusLabel,
        rulesConfirmedAt: r.rules_confirmed_at,
        outcome: r.outcome,
        outcomeAt: r.outcome_at,
      };

      return {
        // grouping keys
        phase: r.phase,
        challengeType: r.challenge_type,
        steps: r.steps,
        platformType: r.platform_type,
        outcome: r.outcome,
        // render + table
        data: card,
        tableStatus,
        rulesConfirmedAt: r.rules_confirmed_at,
        slug: r.slug,
        id: r.id,
        firmName: r.firm_id,
        firmLogo: firmLogoUrl(r.firm_id) ?? "",
        accountSize: card.accountSize,
        balance: card.balance,
        dailyLossPct: r.daily_loss_pct,
        maxDrawdownPct: r.max_drawdown_pct,
        profitTargetPct: r.profit_target_pct,
        pairSlug: r.pair_slug,
      } satisfies AccountView;
    });
  }, [rawAccounts]);

  const groups = useMemo(() => groupAccounts(accounts), [accounts]);

  // No presence pinger here — sleep/wake is pair-level now and the
  // accounts list page is not a "watching one pair" surface.

  const totalActive = accounts.filter((a) => a.outcome == null).length;
  const totalArchived = accounts.filter((a) => a.outcome != null).length;
  const totalFunded = accounts.filter(
    (a) => a.phase === "funded" && a.outcome == null,
  ).length;
  const isEmpty = rawAccounts !== null && rawAccounts.length === 0;
  const isLoading = rawAccounts === null && !loadError;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Accounts"
        subtitle={
          isLoading
            ? "Loading…"
            : `${totalActive} Active · ${totalArchived} Archived · ${totalFunded} Funded`
        }
        icon={<CoolIcon name="Credit_Card_02" />}
        accentWords={["Accounts"]}
        right={<AddAccountButton onComplete={refetch} />}
      />

      {loadError && (
        <DashboardSection>
          <div className="text-sm text-destructive">
            Failed to load accounts: {loadError}
          </div>
        </DashboardSection>
      )}

      {isEmpty && (
        <DashboardSection>
          <div className="flex flex-col items-center gap-4 py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/10">
              <Wallet className="h-6 w-6 text-accent" />
            </div>
            <div>
              <h2 className="text-lg font-medium">No accounts yet</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Add your first prop firm account — we&rsquo;ll log in to verify
                it works, no trades.
              </p>
            </div>
            <AddAccountButton
              onComplete={refetch}
              label="Add your first account"
            />
          </div>
        </DashboardSection>
      )}

      {!isEmpty && !isLoading && (
        <TabNav tabs={TABS} active={tab} onChange={setTab} />
      )}

      {/* ── Grouped (default) ───────────────────────────── */}
      {!isEmpty && !isLoading && tab === "grouped" && (
        <div className="flex flex-col gap-4">
          {groups.map((group) => (
            <ChallengeGroupSection key={group.category} group={group} />
          ))}
        </div>
      )}

      {/* ── Table (full history) ────────────────────────── */}
      {!isEmpty && !isLoading && tab === "table" && (
        <DashboardSection>
          <AccountsTable
            accounts={accounts}
            matchingId={matchingId}
            onFindPartner={(acc) => void handleFindPartner(acc)}
          />
        </DashboardSection>
      )}
    </div>
  );
}
